import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Table, Tag, Progress, Tabs, Empty, Spin, Button, App, Space, Tooltip, Input, Select, DatePicker, Row, Col, Dropdown, Collapse, Badge, Modal, Form, Upload } from 'antd';
import { FileSignature, Clock, CheckCircle2, XCircle, Send, Trash2, AlertCircle, Search, X, Download, MoreVertical, Filter as FilterIcon, Plus, Calendar, Pencil, Archive, RotateCcw, Paperclip } from 'lucide-react';
import dayjs, { type Dayjs } from 'dayjs';
import { signingService } from '@/services/signingService';
import { taskService } from '@/services/taskService';
import { documentService, type DocumentCategory } from '@/services/documentService';
import { CategoryTreeSelect } from '@/components/documents/CategoryTreeSelect';
import { PageHeader, Card } from '@/components/common/PageHeader';
import { useAuthStore } from '@/store/authStore';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import type { SignatureRequest } from '@/types';
import { useState, useMemo, useEffect } from 'react';
import { useColumnConfig, type ColumnDef } from '@/hooks/useColumnConfig';

type Filter = 'pending' | 'sent-by-me' | 'completed' | 'rejected' | 'archived';

const WORKFLOW_LABELS: Record<string, string> = {
  SEQUENTIAL: 'Tuần tự', PARALLEL: 'Song song',
};
const STATUS_LABELS_SIGN: Record<string, string> = {
  PENDING: 'Đang chờ', SIGNED: 'Đã ký xong', REJECTED: 'Từ chối', REVOKED: 'Đã thu hồi',
};

const ALL_SIG_COLUMNS: ColumnDef<SignatureRequest>[] = [
  {
    // V1.0.64: STT — số thứ tự theo vị trí trong bảng hiện tại
    key: '_stt', label: 'STT', defaultVisible: true, width: 60,
    render: (_: any, _row: any, index?: number) => (
      <span style={{ color: '#7d8a9e', fontSize: 12 }}>{(index ?? 0) + 1}</span>
    ),
    excelValue: (_r, i) => String((i ?? 0) + 1),
  },
  {
    // V1.0.63h: Set width cố định + ellipsis 2 dòng để không co lại quá hẹp
    key: 'documentName', label: 'Tài liệu', defaultVisible: true, width: 260,
    render: (name: string, row: SignatureRequest) => (
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 500, lineHeight: 1.4,
          display: 'flex', alignItems: 'flex-start', gap: 6,
          // Cho phép wrap nhưng không break từ giữa chừng
          wordBreak: 'break-word',
        }}>
          <FileSignature size={13} style={{ marginTop: 3, flexShrink: 0, color: '#6699CC' }} />
          <span style={{
            display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden', textOverflow: 'ellipsis',
            wordBreak: 'break-word',
          }}>{name}</span>
        </div>
        <div className="mono" style={{
          fontSize: 11, color: '#7d8a9e', marginTop: 2, marginLeft: 19,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {row.documentId}
        </div>
      </div>
    ),
    excelValue: (r) => r.documentName,
  },
  {
    // V1.0.46: Mô tả yêu cầu ký — hiển thị mặc định
    key: 'title', label: 'Mô tả yêu cầu ký', defaultVisible: true, width: 280,
    render: (_: any, row: any) => {
      const title = row.title;
      const desc = row.taskDescription;
      if (!title && !desc) return <span style={{ color: '#a5b0c0' }}>—</span>;
      return (
        <div>
          {title && (
            <div style={{ fontWeight: 500, fontSize: 13, color: '#1e293b', lineHeight: 1.4 }}>
              {title}
            </div>
          )}
          {desc && (
            <div style={{
              fontSize: 12, color: '#5b6875', marginTop: title ? 2 : 0,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', lineHeight: 1.4,
            }}>
              {desc}
            </div>
          )}
        </div>
      );
    },
    excelValue: (r: any) => [r.title, r.taskDescription].filter(Boolean).join(' — '),
  },
  {
    key: 'categoryName', label: 'Loại VB', defaultVisible: false, width: 120,
    render: (_: any, row: any) => row.categoryName ?? '-',
    excelValue: (r: any) => r.categoryName ?? '',
  },
  {
    key: 'workflow', label: 'Luồng ký', defaultVisible: true, width: 110,
    render: (w: string) => (
      <Tag color={w === 'SEQUENTIAL' ? 'blue' : 'purple'}>
        {WORKFLOW_LABELS[w] ?? w}
      </Tag>
    ),
    excelValue: (r) => WORKFLOW_LABELS[r.workflow] ?? r.workflow,
  },
  {
    key: 'status', label: 'Trạng thái', defaultVisible: true, width: 110,
    render: (s: string) => {
      const color: Record<string, string> = {
        PENDING: 'processing', SIGNED: 'success', REJECTED: 'error', REVOKED: 'default',
      };
      return <Tag color={color[s] ?? 'default'}>{STATUS_LABELS_SIGN[s] ?? s}</Tag>;
    },
    excelValue: (r) => STATUS_LABELS_SIGN[r.status ?? ''] ?? r.status ?? '',
  },
  {
    key: 'progress', label: 'Tiến độ ký', defaultVisible: true, width: 150,
    render: (_: any, row: SignatureRequest) => {
      const total = row.signers?.length ?? 0;
      const signed = row.signers?.filter((s: any) => s.status === 'SIGNED').length ?? 0;
      const percent = total > 0 ? Math.round((signed / total) * 100) : 0;
      return (
        <div>
          <Progress percent={percent} size="small" showInfo={false} />
          <div style={{ fontSize: 11, color: '#a5b0c0', marginTop: 2 }}>
            {signed}/{total} người ký
          </div>
        </div>
      );
    },
    excelValue: (r) => {
      const total = r.signers?.length ?? 0;
      const signed = r.signers?.filter((s: any) => s.status === 'SIGNED').length ?? 0;
      return `${signed}/${total}`;
    },
  },
  {
    // V1.0.66: Lượt ký của user hiện tại + có đến lượt mình chưa
    key: 'myTurn', label: 'Lượt ký của tôi', defaultVisible: true, width: 150,
    render: (_: any, row: SignatureRequest) => {
      if (row.mySignOrder == null) {
        return <span style={{ fontSize: 11, color: '#a5b0c0' }}>—</span>;
      }
      const orderText = `Lượt ${row.mySignOrder}${row.totalSigners ? `/${row.totalSigners}` : ''}`;
      let badge: React.ReactNode = null;
      if (row.isMyTurn) {
        badge = <Tag color="processing" style={{ marginInlineEnd: 0 }}>Đến lượt bạn</Tag>;
      } else if (row.mySignerStatus === 'SIGNED') {
        badge = <Tag color="success" style={{ marginInlineEnd: 0 }}>Đã ký</Tag>;
      } else if (row.mySignerStatus === 'REJECTED') {
        badge = <Tag color="error" style={{ marginInlineEnd: 0 }}>Đã từ chối</Tag>;
      } else if (row.mySignerStatus === 'PENDING') {
        badge = <Tag color="default" style={{ marginInlineEnd: 0 }}>Chờ lượt trước</Tag>;
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: row.isMyTurn ? '#1F3864' : '#5b6875' }}>
            {orderText}
          </span>
          {badge}
        </div>
      );
    },
    excelValue: (r: any) => {
      if (r.mySignOrder == null) return '';
      const status = r.isMyTurn ? 'Đến lượt bạn'
        : r.mySignerStatus === 'SIGNED' ? 'Đã ký'
        : r.mySignerStatus === 'REJECTED' ? 'Đã từ chối'
        : r.mySignerStatus === 'PENDING' ? 'Chờ lượt trước' : '';
      return `Lượt ${r.mySignOrder}${r.totalSigners ? `/${r.totalSigners}` : ''}${status ? ` (${status})` : ''}`;
    },
  },
  {
    // V1.0.64: Đổi label "Người gửi" → "Người trình ký" (rõ nghĩa hơn), bật mặc định
    key: 'creator', label: 'Người trình ký', defaultVisible: true, width: 160,
    render: (_: any, row: any) => row.createdByName ?? '-',
    excelValue: (r: any) => r.createdByName ?? '',
  },
  {
    key: 'dueDate', label: 'Thời hạn', defaultVisible: true, width: 130,
    render: (d: string | undefined, row: SignatureRequest) => {
      if (!d) return <span style={{ fontSize: 11, color: '#a5b0c0' }}>—</span>;
      const due = dayjs(d);
      const isOverdue = due.isBefore(dayjs()) && row.status === 'PENDING';
      return (
        <Tooltip title={due.format('DD/MM/YYYY HH:mm')}>
          <span style={{
            fontSize: 11,
            color: isOverdue ? '#ff4d4f' : '#5b6875',
            fontWeight: isOverdue ? 600 : 400,
          }}>
            {isOverdue && <AlertCircle size={11} style={{ verticalAlign: -1, marginRight: 3 }} />}
            {due.format('DD/MM HH:mm')}
            {isOverdue && <div style={{ fontSize: 10 }}>(quá hạn)</div>}
          </span>
        </Tooltip>
      );
    },
    excelValue: (r) => r.dueDate ? dayjs(r.dueDate).format('DD/MM/YYYY HH:mm') : '',
  },
  {
    key: 'createdAt', label: 'Ngày tạo', defaultVisible: true, width: 110,
    render: (t: string) => (
      <span className="mono" style={{ fontSize: 11, color: '#a5b0c0' }}>
        {dayjs(t).format('DD/MM/YYYY')}
      </span>
    ),
    excelValue: (r) => dayjs(r.createdAt).format('DD/MM/YYYY HH:mm'),
  },
];

export function SigningListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();
  const { user: currentUser } = useAuthStore();
  const { isMobile } = useBreakpoint();
  const [filter, setFilter] = useState<Filter>('pending');
  const [showFilters, setShowFilters] = useState(false);  // V1.0.63h: toggle filter trên mobile
  const [editFor, setEditFor] = useState<SignatureRequest | null>(null);  // V1.0.63i: modal sửa/gắn task

  const { antColumns, exportExcel, ColumnConfigButton, ColumnConfigModal } =
    useColumnConfig<SignatureRequest>(ALL_SIG_COLUMNS, 'hv-taskflow-signing-columns-v4');

  // ========== SEARCH FILTERS ==========
  const [keyword, setKeyword] = useState('');
  const [categoryId, setCategoryId] = useState<string | undefined>();
  const [dueFrom, setDueFrom] = useState<Dayjs | null>(null);
  const [dueTo, setDueTo] = useState<Dayjs | null>(null);
  // V1.0.66: chỉ hiện các phiếu đang ĐẾN LƯỢT tôi ký — MẶC ĐỊNH BẬT (theo yêu cầu)
  const [onlyMyTurn, setOnlyMyTurn] = useState(true);
  // V1.0.66: lọc theo người trình ký (createdBy)
  const [creatorId, setCreatorId] = useState<string | undefined>();

  const { data = [], isLoading } = useQuery({
    queryKey: ['signing', filter],
    queryFn: () => signingService.list(filter as any),
  });

  const { data: categories = [] } = useQuery<DocumentCategory[]>({
    queryKey: ['doc-categories'],
    queryFn: () => documentService.listCategories(),
    staleTime: 5 * 60 * 1000,
  });

  // V1.0.63i: Số đếm bản ghi cho 4 tab
  const { data: tabCounts } = useQuery({
    queryKey: ['signing-counts'],
    queryFn: () => signingService.getCounts(),
    staleTime: 30 * 1000,
  });

  // Apply client-side filters
  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return (data as any[]).filter((row: any) => {
      // Keyword: tìm trong title + task description + người trình ký (nếu có)
      if (kw) {
        const hay = [row.title, row.taskDescription, row.documentTitle, row.createdByName]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(kw)) return false;
      }
      // Category (loại văn bản)
      if (categoryId && row.categoryId !== categoryId) return false;
      // V1.0.66: lọc theo người trình ký
      if (creatorId && row.createdBy !== creatorId) return false;
      // V1.0.66: chỉ hiện việc đang đến lượt tôi ký — chỉ áp dụng ở các tab có ý nghĩa
      // (Chờ tôi ký / Hoàn thành / Từ chối). Các tab khác (Tôi gửi, Lưu trữ) bỏ qua cờ này.
      const myTurnApplicable = filter === 'pending' || filter === 'completed' || filter === 'rejected';
      if (onlyMyTurn && myTurnApplicable && !row.isMyTurn) return false;
      // Due date range
      if (row.dueDate) {
        const due = dayjs(row.dueDate);
        if (dueFrom && due.isBefore(dueFrom, 'day')) return false;
        if (dueTo && due.isAfter(dueTo, 'day')) return false;
      } else {
        // Không có due date → nếu filter due thì loại
        if (dueFrom || dueTo) return false;
      }
      return true;
    });
  }, [data, keyword, categoryId, creatorId, dueFrom, dueTo, onlyMyTurn, filter]);

  // V1.0.66: Danh sách người trình ký (distinct) lấy từ dữ liệu đang tải — cho dropdown lọc
  const creatorOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of (data as any[])) {
      if (row.createdBy) map.set(row.createdBy, row.createdByName || 'Không rõ');
    }
    return Array.from(map, ([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'vi'));
  }, [data]);

  const clearFilters = () => {
    setKeyword('');
    setCategoryId(undefined);
    setCreatorId(undefined);
    setDueFrom(null);
    setDueTo(null);
    setOnlyMyTurn(false);
  };
  const hasFilters = keyword || categoryId || creatorId || dueFrom || dueTo || onlyMyTurn;

  const counts = {
    pending: filter === 'pending' ? filtered.length : undefined,
    sent: filter === 'sent-by-me' ? filtered.length : undefined,
  };

  const handleDelete = (row: SignatureRequest) => {
    const signedCount = row.signers?.filter((s: any) => s.status === 'SIGNED').length ?? 0;
    if (signedCount > 0) {
      message.warning('Không thể xóa: đã có người ký trong luồng');
      return;
    }
    modal.confirm({
      title: 'Xóa yêu cầu ký?',
      content: (
        <div>
          <p>Bạn có chắc muốn xóa yêu cầu ký <b>{row.documentName}</b>?</p>
          <p style={{ fontSize: 12, color: '#8a95a5' }}>
            Hành động này không thể hoàn tác. Chỉ xóa được khi chưa có ai ký.
          </p>
        </div>
      ),
      okText: 'Xóa',
      okButtonProps: { danger: true },
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await signingService.delete(row.id);
          message.success('Đã xóa yêu cầu ký');
          queryClient.invalidateQueries({ queryKey: ['signing'] });
        } catch (e: any) {
          message.error(e.response?.data?.message ?? 'Không thể xóa: ' + e.message);
        }
      },
    });
  };

  // V1.0.65: Đưa phiếu vào lưu trữ
  const handleArchive = (row: SignatureRequest) => {
    modal.confirm({
      title: 'Đưa phiếu vào lưu trữ?',
      content: (
        <div>
          <p>Phiếu <b>{row.documentName}</b> sẽ được chuyển vào kho lưu trữ.</p>
          <p style={{ fontSize: 12, color: '#8a95a5' }}>
            Phiếu sẽ ẩn khỏi các tab chính. Anh có thể xem lại / khôi phục trong tab "Lưu trữ".
          </p>
        </div>
      ),
      okText: 'Lưu trữ',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await signingService.archive(row.id);
          message.success('Đã chuyển vào lưu trữ');
          queryClient.invalidateQueries({ queryKey: ['signing'] });
        } catch (e: any) {
          message.error(e.response?.data?.message ?? 'Không thể lưu trữ: ' + e.message);
        }
      },
    });
  };

  // V1.0.65: Khôi phục phiếu từ lưu trữ
  const handleUnarchive = (row: SignatureRequest) => {
    modal.confirm({
      title: 'Khôi phục phiếu?',
      content: `Phiếu "${row.documentName}" sẽ được trả về danh sách chính.`,
      okText: 'Khôi phục',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await signingService.unarchive(row.id);
          message.success('Đã khôi phục phiếu');
          queryClient.invalidateQueries({ queryKey: ['signing'] });
        } catch (e: any) {
          message.error(e.response?.data?.message ?? 'Không thể khôi phục: ' + e.message);
        }
      },
    });
  };

  // Kết hợp antColumns (từ config) + cột action cố định cuối
  const columnsWithAction = useMemo(() => [
    ...antColumns,
    {
      title: '',
      dataIndex: '_action',
      width: 180,
      fixed: 'right' as const,
      render: (_: any, row: SignatureRequest) => {
        const signedCount = row.signers?.filter((s: any) => s.status === 'SIGNED').length ?? 0;
        // V1.0.46: Cho phép xóa từ MỌI tab nếu là creator + chưa ai ký + status PENDING
        const isCreator = currentUser?.id && row.createdBy === currentUser.id;
        const canDelete = isCreator && signedCount === 0 && row.status === 'PENDING';
        // V1.0.65: Archive logic (relaxed)
        //   - canArchive: creator + chưa archived + 1 trong 3 điều kiện:
        //     (a) status SIGNED hoặc REJECTED
        //     (b) status PENDING NHƯNG không có signer NÀO KHÁC creator đã ký
        //         (creator có thể đã tự ký, hoặc chưa ai ký — đều cho)
        //   - canUnarchive: đang ở tab archived
        const otherSignedCount = (row.signers ?? []).filter((s: any) => {
          const signerId = s.user?.id ?? s.userId;
          return s.status === 'SIGNED' && signerId !== row.createdBy;
        }).length;
        const canArchive = isCreator
          && filter !== 'archived'
          && (
            row.status === 'SIGNED'
            || row.status === 'REJECTED'
            || (row.status === 'PENDING' && otherSignedCount === 0)
          );
        const canUnarchive = isCreator && filter === 'archived';

        return (
          <Space size={4}>
            <Button size="small" type="primary" onClick={() => navigate(`/signing/${row.id}`)}>
              {filter === 'pending' ? 'Ký' : 'Xem'}
            </Button>
            {isCreator && filter !== 'archived' && (
              <Tooltip title="Sửa thông tin / Gắn vào task">
                <Button size="small" icon={<Pencil size={12} />}
                  onClick={() => setEditFor(row)}
                />
              </Tooltip>
            )}
            {canArchive && (
              <Tooltip title="Đưa phiếu vào lưu trữ">
                <Button size="small" icon={<Archive size={12} />}
                  onClick={() => handleArchive(row)}
                />
              </Tooltip>
            )}
            {canUnarchive && (
              <Tooltip title="Khôi phục từ lưu trữ">
                <Button size="small" icon={<RotateCcw size={12} />}
                  onClick={() => handleUnarchive(row)}
                />
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Xóa yêu cầu ký (chỉ khi chưa ai ký)">
                <Button size="small" danger icon={<Trash2 size={12} />}
                  onClick={() => handleDelete(row)}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ], [antColumns, filter, currentUser?.id]);

  const handleExport = () => {
    if (filtered.length === 0) { message.warning('Không có dữ liệu để xuất'); return; }
    exportExcel(filtered, 'Danh sách ký', `signing_${filter}`);
    message.success(`Đã xuất ${filtered.length} yêu cầu`);
  };

  return (
    <>
      <PageHeader
        title="Ký điện tử"
        subtitle={isMobile ? undefined : "Quản lý các yêu cầu ký tài liệu"}
        extra={
          isMobile ? (
            // V1.0.63h: Mobile - chỉ hiện 1 nút + dropdown menu
            <Space size={6}>
              <Button
                type="primary"
                icon={<Plus size={14} />}
                onClick={() => navigate('/signing/new')}
                size="middle"
              >
                Trình ký
              </Button>
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    { key: 'export', label: 'Xuất Excel', icon: <Download size={14} />, onClick: handleExport },
                    { key: 'columns', label: 'Cài đặt cột', onClick: () => {
                      // Trigger ColumnConfigButton click programmatically
                      document.querySelector<HTMLButtonElement>('.column-config-trigger')?.click();
                    }},
                  ],
                }}
              >
                <Button icon={<MoreVertical size={16} />} />
              </Dropdown>
            </Space>
          ) : (
            <>
              <ColumnConfigButton />
              <Button icon={<Download size={14} />} onClick={handleExport}>Xuất Excel</Button>
              <Button type="primary" icon={<FileSignature size={14} />}
                onClick={() => navigate('/signing/new')}>
                Trình ký mới
              </Button>
            </>
          )
        }
      />
      <Card padding={0}>
        <Tabs
          activeKey={filter}
          onChange={(k) => setFilter(k as Filter)}
          style={{ padding: isMobile ? '0 10px' : '0 20px', borderBottom: '1px solid #3a4050' }}
          size={isMobile ? 'small' : 'middle'}
          tabBarGutter={isMobile ? 8 : 16}
          items={[
            {
              key: 'pending',
              label: <span>
                <Clock size={12} style={{ verticalAlign: -1, marginRight: 4 }} />
                {isMobile ? 'Chờ ký' : 'Chờ tôi ký'}
                {tabCounts && tabCounts.pending > 0 ? ` (${tabCounts.pending})` : ''}
              </span>,
            },
            {
              key: 'sent-by-me',
              label: <span>
                <Send size={12} style={{ verticalAlign: -1, marginRight: 4 }} />
                {isMobile ? 'Đã gửi' : 'Tôi đã gửi'}
                {tabCounts && tabCounts.sentByMe > 0 ? ` (${tabCounts.sentByMe})` : ''}
              </span>,
            },
            {
              key: 'completed',
              label: <span>
                <CheckCircle2 size={12} style={{ verticalAlign: -1, marginRight: 4 }} />
                {isMobile ? 'Xong' : 'Hoàn thành'}
                {tabCounts && tabCounts.completed > 0 ? ` (${tabCounts.completed})` : ''}
              </span>,
            },
            {
              key: 'rejected',
              label: <span>
                <XCircle size={12} style={{ verticalAlign: -1, marginRight: 4 }} />
                {isMobile ? 'TC' : 'Từ chối'}
                {tabCounts && tabCounts.rejected > 0 ? ` (${tabCounts.rejected})` : ''}
              </span>,
            },
            {
              // V1.0.65: Tab Lưu trữ — chỉ phiếu mà tôi tạo và đã đưa vào lưu trữ
              key: 'archived',
              label: <span>
                <Archive size={12} style={{ verticalAlign: -1, marginRight: 4 }} />
                {isMobile ? 'LT' : 'Lưu trữ'}
                {tabCounts && tabCounts.archived > 0 ? ` (${tabCounts.archived})` : ''}
              </span>,
            },
          ]}
        />

        {/* ===== SEARCH BAR ===== */}
        <div style={{
          padding: isMobile ? '10px 12px' : '12px 20px',
          borderBottom: '1px solid #eceef1',
          background: '#fafbfc',
        }}>
          {isMobile ? (
            // V1.0.63h: Mobile - search + nút filter toggle
            <>
              <div style={{ display: 'flex', gap: 8 }}>
                <Input
                  allowClear
                  prefix={<Search size={13} style={{ color: '#8a95a5' }} />}
                  placeholder="Tìm kiếm..."
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  style={{ flex: 1 }}
                />
                <Badge
                  count={(categoryId ? 1 : 0) + (creatorId ? 1 : 0) + (dueFrom ? 1 : 0) + (dueTo ? 1 : 0)}
                  size="small"
                  offset={[-4, 4]}
                >
                  <Button
                    icon={<FilterIcon size={14} />}
                    onClick={() => setShowFilters(v => !v)}
                    type={showFilters ? 'primary' : 'default'}
                  />
                </Badge>
              </div>
              {showFilters && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <CategoryTreeSelect
                    categories={categories}
                    placeholder="Loại văn bản"
                    value={categoryId}
                    onChange={setCategoryId}
                  />
                  <Select
                    allowClear
                    showSearch
                    placeholder="Người trình ký"
                    style={{ width: '100%' }}
                    value={creatorId}
                    onChange={setCreatorId}
                    options={creatorOptions}
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                    notFoundContent="Không có người trình ký"
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <DatePicker
                      placeholder="Hạn từ"
                      format="DD/MM/YYYY"
                      style={{ flex: 1 }}
                      value={dueFrom}
                      onChange={setDueFrom}
                      inputReadOnly
                    />
                    <DatePicker
                      placeholder="Hạn đến"
                      format="DD/MM/YYYY"
                      style={{ flex: 1 }}
                      value={dueTo}
                      onChange={setDueTo}
                      inputReadOnly
                    />
                  </div>
                  {hasFilters && (
                    <Button icon={<X size={13} />} onClick={clearFilters} block size="small">
                      Xóa lọc
                    </Button>
                  )}
                </div>
              )}
            </>
          ) : (
            // Desktop: layout cũ
            <Row gutter={8} align="middle">
              <Col xs={24} md={6}>
                <Input
                  allowClear
                  prefix={<Search size={13} style={{ color: '#8a95a5' }} />}
                  placeholder="Tìm theo tiêu đề, nội dung, người trình ký..."
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                />
              </Col>
              <Col xs={12} md={4}>
                <CategoryTreeSelect
                  categories={categories}
                  placeholder="Loại văn bản"
                  value={categoryId}
                  onChange={setCategoryId}
                />
              </Col>
              <Col xs={12} md={4}>
                <Select
                  allowClear
                  showSearch
                  placeholder="Người trình ký"
                  style={{ width: '100%' }}
                  value={creatorId}
                  onChange={setCreatorId}
                  options={creatorOptions}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  notFoundContent="Không có người trình ký"
                />
              </Col>
              <Col xs={12} md={3}>
                <DatePicker
                  placeholder="Hạn ký từ"
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  value={dueFrom}
                  onChange={setDueFrom}
                />
              </Col>
              <Col xs={12} md={3}>
                <DatePicker
                  placeholder="Hạn ký đến"
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  value={dueTo}
                  onChange={setDueTo}
                />
              </Col>
              <Col xs={12} md={4}>
                {hasFilters && (
                  <Button icon={<X size={13} />} onClick={clearFilters} block>
                    Xóa lọc
                  </Button>
                )}
              </Col>
            </Row>
          )}
          {/* V1.0.66: Lọc nhanh các phiếu đang đến lượt tôi ký */}
          {(filter === 'pending' || filter === 'completed' || filter === 'rejected') && (
            <div style={{ marginTop: 8 }}>
              <Button
                size="small"
                type={onlyMyTurn ? 'primary' : 'default'}
                icon={<Clock size={13} style={{ verticalAlign: -2 }} />}
                onClick={() => setOnlyMyTurn(v => !v)}
              >
                Chỉ việc đến lượt tôi ký
              </Button>
            </div>
          )}
          {hasFilters && (
            <div style={{ marginTop: 8, fontSize: 11, color: '#7d8a98' }}>
              Đang hiển thị {filtered.length} / {data.length} kết quả
            </div>
          )}
        </div>

        {isLoading ? (
          <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>
        ) : filtered.length === 0 ? (
          <Empty
            description={
              hasFilters ? 'Không có kết quả khớp với bộ lọc' :
              filter === 'pending' ? 'Không có yêu cầu nào chờ bạn ký' :
              filter === 'sent-by-me' ? 'Bạn chưa gửi yêu cầu ký nào' :
              filter === 'completed' ? 'Chưa có yêu cầu nào hoàn thành' :
              'Không có yêu cầu từ chối'
            }
            style={{ padding: isMobile ? 30 : 60 }}
          />
        ) : isMobile ? (
          // V1.0.63h: Mobile - Card list thay vì Table
          <div style={{ padding: '8px 10px' }}>
            {filtered.map((row: any) => (
              <SigningMobileCard
                key={row.id}
                row={row}
                filter={filter}
                currentUserId={currentUser?.id}
                onOpen={() => navigate(`/signing/${row.id}`)}
                onDelete={() => handleDelete(row)}
              />
            ))}
            <div style={{ textAlign: 'center', padding: 12, fontSize: 11, color: '#8a95a5' }}>
              Hiển thị {filtered.length} yêu cầu
            </div>
          </div>
        ) : (
          <Table columns={columnsWithAction} dataSource={filtered} rowKey="id" size="middle"
            scroll={{ x: 1200 }} pagination={{ pageSize: 20 }}
          />
        )}
      </Card>
      <ColumnConfigModal />
      {editFor && (
        <EditSignatureInfoModal
          request={editFor}
          categories={categories}
          onClose={() => setEditFor(null)}
          onSuccess={() => {
            setEditFor(null);
            queryClient.invalidateQueries({ queryKey: ['signatures'] });
            message.success('Đã cập nhật thông tin yêu cầu ký');
          }}
        />
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════
// V1.0.63i: Modal sửa thông tin yêu cầu ký + gắn vào task
// ════════════════════════════════════════════════════════════
function EditSignatureInfoModal({ request, categories, onClose, onSuccess }: {
  request: SignatureRequest;
  categories: DocumentCategory[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [tasks, setTasks] = useState<any[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  // V1.0.66: tài liệu tham chiếu — thêm mới (newFiles) + gỡ bớt khỏi danh sách hiện có (removedIds)
  const [newFiles, setNewFiles] = useState<any[]>([]);
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const existingRelated = (request.relatedAttachments ?? []).filter(a => !removedIds.includes(a.id));

  useEffect(() => {
    form.setFieldsValue({
      title: request.title,
      description: request.taskDescription,
      categoryId: request.categoryId,
      dueDate: request.dueDate ? dayjs(request.dueDate) : undefined,
      taskId: request.taskId ?? undefined,
      confidentiality: request.confidentiality ?? 'INTERNAL',
    });
    // Load danh sách task của user để chọn gắn
    (async () => {
      setLoadingTasks(true);
      try {
        // Lấy task tôi tạo / được giao để gắn
        const res = await taskService.list({ tab: 'assigned-by-me', pageSize: 200 } as any);
        const res2 = await taskService.list({ tab: 'assigned-to-me', pageSize: 200 } as any);
        const merged = [...(res.items ?? []), ...(res2.items ?? [])];
        // Dedupe theo id
        const map = new Map<string, any>();
        merged.forEach(t => map.set(t.id, t));
        setTasks(Array.from(map.values()));
      } catch {
        // ignore
      } finally { setLoadingTasks(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.id]);

  const submit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      await signingService.updateInfo(request.id, {
        taskId: values.taskId ?? null,
        title: values.title,
        description: values.description ?? null,
        categoryId: values.categoryId ?? null,
        dueDate: values.dueDate ? values.dueDate.toISOString() : null,
        confidentiality: values.confidentiality ?? null,
      });

      // V1.0.66: Gỡ các tài liệu tham chiếu đã bỏ
      for (const rid of removedIds) {
        try { await signingService.removeRelatedAttachment(request.id, rid); }
        catch (err) { console.warn('Gỡ tài liệu tham chiếu lỗi:', err); }
      }

      // V1.0.66: Upload + đính kèm các tài liệu tham chiếu mới
      if (newFiles.length > 0) {
        const addedIds: string[] = [];
        for (const f of newFiles) {
          const fileObj = f.originFileObj as File;
          if (!fileObj) continue;
          try {
            const att = await signingService.quickAttachment(fileObj);
            addedIds.push(att.id);
          } catch (err) {
            console.warn('Upload tài liệu tham chiếu lỗi:', err);
          }
        }
        if (addedIds.length > 0) {
          await signingService.addRelatedAttachments(request.id, addedIds);
        }
      }

      onSuccess();
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal
      open
      title={<span><Pencil size={14} style={{ verticalAlign: -2, marginRight: 8 }} />Sửa thông tin yêu cầu ký</span>}
      onCancel={onClose}
      onOk={submit}
      confirmLoading={saving}
      okText="Lưu"
      cancelText="Hủy"
      width={520}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
        <Form.Item name="title" label="Tiêu đề tài liệu"
          extra="Tên ngắn gọn của tài liệu. Đây cũng là TÊN tài liệu khi tự lưu vào Quản lý tài liệu sau khi ký xong.">
          <Input placeholder="VD: Quyết định bổ nhiệm..." maxLength={500} />
        </Form.Item>

        <Form.Item name="description" label="Mô tả tài liệu"
          extra="Mô tả nội dung và mục đích tài liệu.">
          <Input.TextArea rows={2} placeholder="Nhập mô tả nội dung, mục đích..." maxLength={1000} showCount />
        </Form.Item>

        <Form.Item name="confidentiality" label="Mức công khai (khi lưu vào Quản lý tài liệu)"
          extra="Áp dụng cho tài liệu đã ký được tự đẩy vào kho sau khi hoàn tất ký.">
          <Select
            options={[
              { value: 'PUBLIC', label: 'Công khai — mọi người xem được' },
              { value: 'INTERNAL', label: 'Nội bộ — theo phòng/khoa' },
              { value: 'SHARED', label: 'Chia sẻ — người được cấp quyền' },
              { value: 'CONFIDENTIAL', label: 'Mật — chỉ chủ sở hữu & quản trị' },
            ]}
          />
        </Form.Item>

        <Form.Item name="taskId" label="Gắn vào Task (để theo dõi & quản lý)"
          extra="Khi gắn task, tài liệu sẽ được chuyển vào task đó để theo dõi cùng.">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            loading={loadingTasks}
            placeholder="Chọn task để gắn..."
            options={tasks.map(t => ({
              value: t.id,
              label: `${t.code ? `[${t.code}] ` : ''}${t.title}`,
            }))}
          />
        </Form.Item>

        <Form.Item name="categoryId" label="Loại văn bản">
          <CategoryTreeSelect
            categories={categories}
            placeholder="Chọn loại văn bản"
            value={form.getFieldValue('categoryId')}
            onChange={(v) => form.setFieldValue('categoryId', v)}
          />
        </Form.Item>

        <Form.Item name="dueDate" label="Hạn ký">
          <DatePicker
            format="DD/MM/YYYY"
            style={{ width: '100%' }}
            placeholder="Chọn hạn ký"
          />
        </Form.Item>

        {/* V1.0.66: Tài liệu tham chiếu — đính kèm thêm, hiển thị lại trong trang ký */}
        <Form.Item label="Tài liệu tham chiếu (tùy chọn)"
          extra="Tài liệu đính kèm để người ký xem tham chiếu trước khi ký (không phải tài liệu ký). Hỗ trợ mọi định dạng.">
          {existingRelated.length > 0 && (
            <div style={{ marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {existingRelated.map(a => (
                <div key={a.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 10px', background: '#f5f7fa', borderRadius: 6,
                }}>
                  <Paperclip size={14} style={{ color: '#5b6875', flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 13, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.fileName}
                  </span>
                  <Tooltip title="Gỡ tài liệu này">
                    <Button type="text" size="small" danger
                      icon={<X size={14} />}
                      onClick={() => setRemovedIds(prev => [...prev, a.id])}
                    />
                  </Tooltip>
                </div>
              ))}
            </div>
          )}
          <Upload
            multiple
            fileList={newFiles}
            beforeUpload={() => false}
            onChange={({ fileList }) => setNewFiles(fileList)}
          >
            <Button icon={<Plus size={14} />}>Thêm tài liệu tham chiếu</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════
// V1.0.63h: Mobile Card View cho item trong list signing
// ════════════════════════════════════════════════════════════
interface SigningMobileCardProps {
  row: any;
  filter: Filter;
  currentUserId?: string;
  onOpen: () => void;
  onDelete: () => void;
}

function SigningMobileCard({ row, filter, currentUserId, onOpen, onDelete }: SigningMobileCardProps) {
  const isCreator = row.createdBy === currentUserId;
  const canDelete = isCreator && (row.status === 'PENDING') && (filter === 'sent-by-me');
  const signedCount = row.signers?.filter((s: any) => s.status === 'SIGNED').length ?? 0;
  const totalSigners = row.signers?.length ?? 0;
  // Gộp signers theo user.id để hiện đúng số "người" (không phải số "vị trí")
  const uniqueUserIds = new Set<string>((row.signers ?? []).map((s: any) => s.user?.id));
  const uniqueSignedIds = new Set<string>(
    (row.signers ?? [])
      .filter((s: any) => s.status === 'SIGNED')
      .map((s: any) => s.user?.id)
  );
  const peopleStr = `${uniqueSignedIds.size}/${uniqueUserIds.size} người`;
  const positionStr = totalSigners > uniqueUserIds.size
    ? ` · ${signedCount}/${totalSigners} vị trí`
    : '';

  // Status badge color
  const statusInfo = (() => {
    if (row.status === 'REJECTED') return { color: '#cf1322', bg: '#fff1f0', label: 'Từ chối' };
    if (row.status === 'SIGNED') return { color: '#389e0d', bg: '#f6ffed', label: 'Đã ký xong' };
    if (row.status === 'REVOKED') return { color: '#8a95a5', bg: '#f5f5f5', label: 'Đã thu hồi' };
    return { color: '#d46b08', bg: '#fff7e6', label: 'Đang chờ' };
  })();

  // Workflow label
  const workflowLabel = row.workflow === 'SEQUENTIAL' ? 'Tuần tự' : 'Song song';

  // Due date - hiện badge cảnh báo nếu quá hạn
  const isDueSoon = row.dueDate && row.status === 'PENDING'
    && dayjs(row.dueDate).diff(dayjs(), 'day') <= 1;
  const isOverdue = row.dueDate && row.status === 'PENDING'
    && dayjs(row.dueDate).isBefore(dayjs(), 'day');

  return (
    <div
      onClick={onOpen}
      style={{
        background: '#fff',
        border: '1px solid #e1e5eb',
        borderRadius: 8,
        padding: 12,
        marginBottom: 8,
        cursor: 'pointer',
        transition: 'box-shadow 0.15s',
      }}
      onTouchStart={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
      onTouchEnd={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Header: tài liệu + status */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
        <FileSignature size={16} style={{ color: '#6699CC', flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', lineHeight: 1.3,
            overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {row.documentName}
          </div>
          {row.title && (
            <div style={{ fontSize: 12, color: '#5a6678', marginTop: 2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.title}
            </div>
          )}
        </div>
        <span style={{
          fontSize: 10, padding: '2px 8px', borderRadius: 10,
          color: statusInfo.color, background: statusInfo.bg,
          fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap',
        }}>
          {statusInfo.label}
        </span>
      </div>

      {/* Progress + info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <Progress
          percent={uniqueUserIds.size > 0 ? Math.round((uniqueSignedIds.size / uniqueUserIds.size) * 100) : 0}
          size="small"
          showInfo={false}
          style={{ flex: 1, margin: 0 }}
          strokeColor={statusInfo.color}
        />
        <span style={{ fontSize: 11, color: '#5a6678', whiteSpace: 'nowrap' }}>
          {peopleStr}{positionStr}
        </span>
      </div>

      {/* Footer: workflow + creator + due date */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontSize: 11, color: '#7d8a98', marginTop: 4,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{workflowLabel}</span>
          {row.creatorName && (
            <>
              <span>·</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis',
                whiteSpace: 'nowrap', maxWidth: 100 }}>
                {row.creatorName}
              </span>
            </>
          )}
        </div>
        {row.dueDate && (
          <span style={{
            display: 'flex', alignItems: 'center', gap: 3,
            color: isOverdue ? '#cf1322' : isDueSoon ? '#d46b08' : '#7d8a98',
            fontWeight: (isOverdue || isDueSoon) ? 600 : 400,
          }}>
            <Calendar size={10} />
            {dayjs(row.dueDate).format('DD/MM')}
            {isOverdue && ' ⚠'}
          </span>
        )}
      </div>

      {/* Action button */}
      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        <Button
          type="primary"
          size="middle"
          block
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
        >
          {filter === 'pending' ? 'Ký ngay' : 'Xem chi tiết'}
        </Button>
        {canDelete && (
          <Button
            danger
            size="middle"
            icon={<Trash2 size={14} />}
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
          />
        )}
      </div>
    </div>
  );
}
